import axios from 'axios'
import config from '../config'
import i18n from '../i18n'
import { getApiAcceptLanguage } from '../helpers/locale'

const API_URL = config.api.API_URL

function resolveTenantSlug(): string | null {
 const fromKey = localStorage.getItem('tenant_slug')
 if (fromKey) return fromKey
 const raw = localStorage.getItem('authUser')
 if (!raw) return null
 try {
 const parsed = JSON.parse(raw) as { tenant_slug?: string }
 return parsed.tenant_slug?.trim() || null
 } catch {
 return null
 }
}

function readStoredPermissionsVersion(): number | undefined {
 const raw = localStorage.getItem('authUser')
 if (!raw) return undefined
 try {
 const parsed = JSON.parse(raw) as { user?: { permissions_version?: number } }
 const v = parsed.user?.permissions_version
 return typeof v === 'number' ? v : undefined
 } catch {
 return undefined
 }
}

function normalizeBranchId(value: unknown): string | null {
 const raw = Array.isArray(value) ? value[0] : value
 if (typeof raw !== 'string' && typeof raw !== 'number') return null
 const branchId = String(raw).trim()
 return branchId || null
}

function branchIdFromParams(params: unknown): string | null {
 if (!params) return null
 if (params instanceof URLSearchParams) return normalizeBranchId(params.get('branch_id'))
 if (typeof params !== 'object') return null
 return normalizeBranchId((params as Record<string, unknown>).branch_id)
}

let permissionsVersionRefreshTimer: ReturnType<typeof setTimeout> | null = null

const axiosInstance = axios.create({
 baseURL: API_URL,
 headers: {
 'Content-Type': 'application/json',
 'Accept': 'application/json',
 'Accept-Language': getApiAcceptLanguage(),
 },
})

axiosInstance.interceptors.request.use(
 (config) => {
 config.headers['Accept-Language'] = getApiAcceptLanguage()
 // Tenant context must be sent on every request (including login before any token exists)
 const tenantSlug = resolveTenantSlug()
 if (tenantSlug) {
 config.headers['X-Tenant-Slug'] = tenantSlug
 } else {
 delete config.headers['X-Tenant-Slug']
 }

 const token = localStorage.getItem('authUser')
 if (token) {
 try {
 const userData = JSON.parse(token) as {
 token?: string
 user?: { current_branch_id?: string | null }
 }
 if (userData.token) {
 config.headers.Authorization = `Bearer ${userData.token}`
 }

 // branch_view_mode: explicit global = no X-Branch-Id (tenant-wide lists).
 // branch: selected branch or user's current_branch_id as fallback.
 // Legacy (no branch_view_mode key): keep previous behavior.
 const requestBranchId = normalizeBranchId(config.headers['X-Branch-Id'])
 const paramsBranchId = branchIdFromParams(config.params)
 const branchViewMode = localStorage.getItem('branch_view_mode')
 const selectedBranchId = localStorage.getItem('selected_branch_id')
 let branchId: string | null = null
 if (branchViewMode === 'global') {
 branchId = null
 } else if (branchViewMode === 'branch') {
 branchId = selectedBranchId || userData.user?.current_branch_id || null
 } else {
 branchId = selectedBranchId || userData.user?.current_branch_id || null
 }
 if (requestBranchId) {
 config.headers['X-Branch-Id'] = requestBranchId
 } else if (paramsBranchId) {
 config.headers['X-Branch-Id'] = paramsBranchId
 } else if (branchId) {
 config.headers['X-Branch-Id'] = branchId
 } else {
 delete config.headers['X-Branch-Id']
 }
 } catch (error) {
 console.error('Error parsing user data:', error)
 }
 }
 return config
 },
 (error) => {
 return Promise.reject(error)
 }
)

axiosInstance.interceptors.response.use(
 (response) => {
 const reqUrl = String(response.config?.url ?? '')
 if (reqUrl.includes('/auth/me') || reqUrl.includes('/auth/login')) {
 return response
 }

 const headerV = response.headers?.['x-permissions-version']
 if (
 headerV !== undefined
 && headerV !== null
 && String(headerV) !== ''
 && localStorage.getItem('authUser')
 ) {
 const local = readStoredPermissionsVersion()
 if (local !== undefined && String(headerV) !== String(local)) {
 if (permissionsVersionRefreshTimer) {
 clearTimeout(permissionsVersionRefreshTimer)
 }
 permissionsVersionRefreshTimer = setTimeout(() => {
 permissionsVersionRefreshTimer = null
 void import('../helpers/Auth').then(({ refreshAuthSession }) => {
 void refreshAuthSession()
 })
 }, 400)
 }
 }
 return response
 },
 (error) => {
 if (error.response?.status === 401) {
 localStorage.removeItem('authUser')
 localStorage.removeItem('protection')
 window.dispatchEvent(new CustomEvent('auth:unauthorized'))
 }

 if (error.response?.status === 403) {
 const code = error.response?.data?.code as string | undefined
 const message = error.response?.data?.message as string | undefined
 if (
 code === 'tenant_suspended' ||
 code === 'subscription_expired' ||
 code === 'plan_feature_disabled' ||
 code === 'plan_limit_exceeded'
 ) {
 if (code === 'tenant_suspended' || code === 'subscription_expired') {
 const raw = localStorage.getItem('authUser')
 if (raw) {
 try {
 const parsed = JSON.parse(raw) as { user?: Record<string, unknown> }
 if (parsed.user) {
 const access = (parsed.user.plan_access as Record<string, unknown> | undefined) ?? {}
 parsed.user.plan_access = {
 ...access,
 can_operate: false,
 is_tenant_active: code !== 'tenant_suspended',
 is_subscription_valid: code !== 'subscription_expired',
 }
 localStorage.setItem('authUser', JSON.stringify(parsed))
 window.dispatchEvent(new Event('auth:updated'))
 }
 } catch {
 /* ignore */
 }
 }
 }
 void import('../helpers/ErrorNotification').then(({ default: showError }) => {
 showError(message || i18n.t('errors.operationNotAllowed'))
 })
 window.dispatchEvent(
 new CustomEvent('plan:access-denied', {
 detail: { code, message },
 }),
 )
 }
 }

 return Promise.reject(error)
 }
)

export default axiosInstance
